import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Loading } from "@/components/common";
import { LoginPage, RegisterPage, ForgotPage, ResetPage } from "@/pages/AuthPages";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Schedule from "@/pages/Schedule";
import JobDetail from "@/pages/JobDetail";
import DocList from "@/pages/DocList";
import DocEditor from "@/pages/DocEditor";
import EstimateDetail from "@/pages/EstimateDetail";
import InvoiceDetail from "@/pages/InvoiceDetail";
import { PublicEstimate, PublicInvoice } from "@/pages/PublicDocs";
import CalculatorPage from "@/pages/Calculator";
import Checklists from "@/pages/Checklists";
import Expenses from "@/pages/Expenses";
import SettingsPage from "@/pages/Settings";
import Billing from "@/pages/Billing";

function Protected({ children }) {
  const { session } = useAuth();
  const loc = useLocation();
  if (session === null) return <div className="p-10"><Loading /></div>;
  if (!session) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return children;
}

function ManagerOnly({ children }) {
  const { isManager } = useAuth();
  return isManager ? children : <Navigate to="/schedule" replace />;
}

function GuestOnly({ children }) {
  const { session } = useAuth();
  if (session === null) return <div className="p-10"><Loading /></div>;
  return session ? <Navigate to="/" replace /> : children;
}

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const M = (el) => <ManagerOnly>{el}</ManagerOnly>;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollTop />
          <Routes>
            <Route path="/login" element={<GuestOnly><LoginPage /></GuestOnly>} />
            <Route path="/register" element={<GuestOnly><RegisterPage /></GuestOnly>} />
            <Route path="/forgot-password" element={<ForgotPage />} />
            <Route path="/reset-password" element={<ResetPage />} />
            <Route path="/e/:token" element={<PublicEstimate />} />
            <Route path="/i/:token" element={<PublicInvoice />} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="schedule" element={<Schedule />} />
              <Route path="jobs/:id" element={<JobDetail />} />
              <Route path="clients" element={M(<Clients />)} />
              <Route path="clients/:id" element={M(<ClientDetail />)} />
              <Route path="estimates" element={M(<DocList kind="estimate" />)} />
              <Route path="estimates/new" element={M(<DocEditor kind="estimate" />)} />
              <Route path="estimates/:id" element={M(<EstimateDetail />)} />
              <Route path="estimates/:id/edit" element={M(<DocEditor kind="estimate" />)} />
              <Route path="invoices" element={M(<DocList kind="invoice" />)} />
              <Route path="invoices/new" element={M(<DocEditor kind="invoice" />)} />
              <Route path="invoices/:id" element={M(<InvoiceDetail />)} />
              <Route path="invoices/:id/edit" element={M(<DocEditor kind="invoice" />)} />
              <Route path="calculator" element={M(<CalculatorPage />)} />
              <Route path="checklists" element={M(<Checklists />)} />
              <Route path="expenses" element={M(<Expenses />)} />
              <Route path="settings" element={M(<SettingsPage />)} />
              <Route path="billing" element={M(<Billing />)} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
